/**
 * Cmd+K Global Document Search Palette (GH#21 SP2 Task 7)
 *
 * Mounted once in App.tsx. Listens for Cmd+K / Ctrl+K anywhere in the app and
 * opens a centered MUI Dialog with a debounced search against `/api/documents/search`.
 *
 * Result-click logic mirrors the documentation tab:
 *   - external_link → open URL in a new tab via the placeholder pattern (popup-blocker safe)
 *   - markdown / mixed → navigate to /dashboard/documentation/d/:slug
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Dialog, DialogContent, TextField, InputAdornment, Typography,
  List, ListItemButton, ListItemText, Chip, CircularProgress, Stack, Divider,
  FormControlLabel, Switch, Tooltip, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ContentType,
  CRITICALITY_LABELS,
  Criticality,
  DOCUMENT_TYPE_LABELS,
  DocumentSummaryResponse,
  EXTERNAL_LINK_PROVIDER_LABELS,
  WebAppRole,
} from 'hvp-shared';
import {
  fetchDocumentBySlug,
  searchDocuments,
} from '../../pages/dashboard/documentation/docs/documentsApi';
import {
  flatNavigationCommands,
  MenuItem,
} from '../../pages/dashboard/components/sidebar/menuConfig';
import { useAppSelector } from '../../hooks';

const CRITICALITY_COLOR: Record<
  Criticality,
  'default' | 'primary' | 'warning' | 'error'
> = {
  [Criticality.informational]: 'default',
  [Criticality.standard]: 'primary',
  [Criticality.important]: 'warning',
  [Criticality.critical]: 'error',
};

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

type NavCommand = MenuItem & { section: string };

/**
 * Each result is either a navigation entry or a single document row. Document
 * rows expose two inline actions ("Información" and "Abrir") so the user can
 * pick between the doc's metadata page and the actual content.
 */
interface PaletteRow {
  kind: 'nav' | 'doc';
  nav?: NavCommand;
  doc?: DocumentSummaryResponse;
  key: string;
}

/** Substring + alias match. Case-insensitive, ignores accents. */
function normalize(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
function navMatches(cmd: NavCommand, term: string): boolean {
  if (!term) return true;
  const haystack = [cmd.label, cmd.section, ...(cmd.aliases ?? [])]
    .map(normalize)
    .join(' ');
  return haystack.includes(term);
}

export function CmdKSearch() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useAppSelector(
    (s: any) => s.authReducer?.user?.role
  ) as WebAppRole | undefined;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const debounced = useDebounced(query, 250);
  const docSearchEnabled = open && debounced.trim().length >= 2;

  const { data: docResults = [], isFetching } = useQuery({
    queryKey: ['document-search', debounced, includeCancelled],
    queryFn: () =>
      searchDocuments(debounced, 8, {
        includeWithdrawn: includeCancelled,
      }),
    enabled: docSearchEnabled,
    staleTime: 30_000,
  });

  const navCommands = useMemo(() => flatNavigationCommands(role), [role]);
  const filteredNav = useMemo(() => {
    const term = normalize(debounced.trim());
    return navCommands.filter((c) => navMatches(c, term)).slice(0, 12);
  }, [navCommands, debounced]);

  const rows = useMemo<PaletteRow[]>(() => {
    const out: PaletteRow[] = filteredNav.map((nav) => ({
      kind: 'nav',
      nav,
      key: `nav:${nav.link}`,
    }));
    docResults.forEach((doc) =>
      out.push({ kind: 'doc', doc, key: `doc:${doc.id}` })
    );
    return out;
  }, [filteredNav, docResults]);

  const navCount = filteredNav.length;
  const results = rows;

  // Reset highlight when results change.
  useEffect(() => {
    setHighlight(0);
  }, [results]);

  // Global Cmd+K / Ctrl+K listener.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Reset state on open/close.
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setHighlight(0);
    }
  }, [open]);

  const handleOpenDocContent = async (doc: DocumentSummaryResponse) => {
    // Pre-open synchronously for popup-blocker safety; we'll reuse for external_link
    // or close it for markdown navigation.
    const placeholder = window.open('about:blank', '_blank');
    setOpen(false);
    try {
      const detail = await queryClient.fetchQuery({
        queryKey: ['document', doc.slug],
        queryFn: () => fetchDocumentBySlug(doc.slug),
      });
      const v = detail.currentVersion;
      if (v && v.contentType === ContentType.external_link && v.externalLink?.url) {
        if (placeholder) placeholder.location.href = v.externalLink.url;
        else window.open(v.externalLink.url, '_blank', 'noopener,noreferrer');
        return;
      }
      placeholder?.close();
      navigate(`/dashboard/documentation/d/${doc.slug}`);
    } catch {
      placeholder?.close();
      navigate(`/dashboard/documentation/d/${doc.slug}`);
    }
  };

  const handleOpenDocInfo = (doc: DocumentSummaryResponse) => {
    setOpen(false);
    navigate(`/dashboard/documentation/d/${doc.slug}`);
  };

  const handleSelectNav = (nav: NavCommand) => {
    setOpen(false);
    navigate(nav.link);
  };

  const handleSelectRow = (row: PaletteRow) => {
    if (row.kind === 'nav' && row.nav) handleSelectNav(row.nav);
    // Default Enter on a doc row → open content (most common intent).
    // Use the inline "Información" button to go to the info card instead.
    else if (row.kind === 'doc' && row.doc) handleOpenDocContent(row.doc);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelectRow(results[highlight]);
    }
  };

  const emptyHint = useMemo(() => {
    if (debounced.trim().length === 0 && navCommands.length === 0)
      return 'Escribe para buscar…';
    if (results.length === 0 && debounced.trim().length < 2 && !docSearchEnabled)
      return null; // we'll show nav anyway
    if (results.length === 0 && isFetching) return null;
    if (results.length === 0) return 'Sin resultados.';
    return null;
  }, [debounced, isFetching, navCommands.length, results.length, docSearchEnabled]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { mt: '10vh', alignSelf: 'flex-start' } }}
    >
      <DialogContent sx={{ p: 1.5 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          autoFocus
          variant="outlined"
          placeholder="Buscar documentos…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: isFetching && docSearchEnabled ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
        />

        {results.length > 0 ? (
          <List dense sx={{ mt: 1, maxHeight: '50vh', overflow: 'auto' }}>
            {results.map((row, i) => {
              const selected = i === highlight;
              if (row.kind === 'nav' && row.nav) {
                return (
                  <ListItemButton
                    key={row.key}
                    selected={selected}
                    onClick={() => handleSelectRow(row)}
                    sx={{ borderRadius: 1, mb: 0.25 }}
                  >
                    {row.nav.icon && (
                      <Box sx={{ mr: 1.5, display: 'flex', color: 'text.secondary' }}>
                        {row.nav.icon}
                      </Box>
                    )}
                    <ListItemText
                      primary={row.nav.label}
                      secondary={row.nav.section}
                    />
                  </ListItemButton>
                );
              }
              if (row.kind === 'doc' && row.doc) {
                const doc = row.doc;
                const isExternal =
                  doc.currentVersion?.contentType === ContentType.external_link;
                return (
                  <ListItemButton
                    key={row.key}
                    selected={selected}
                    onClick={() => handleSelectRow(row)}
                    sx={{ borderRadius: 1, mb: 0.25, pr: 1 }}
                  >
                    <Box sx={{ mr: 1.5, display: 'flex', color: 'text.secondary' }}>
                      <ArticleIcon fontSize="small" />
                    </Box>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                            {doc.title}
                          </Typography>
                          <Chip
                            label={DOCUMENT_TYPE_LABELS[doc.type]}
                            size="small"
                            sx={{ fontSize: 10, height: 18 }}
                          />
                          <Chip
                            label={CRITICALITY_LABELS[doc.criticality]}
                            size="small"
                            color={CRITICALITY_COLOR[doc.criticality]}
                            sx={{ fontSize: 10, height: 18 }}
                          />
                        </Stack>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary">
                          Documento
                          {isExternal && doc.currentVersion?.externalLinkProvider
                            ? ` · ${EXTERNAL_LINK_PROVIDER_LABELS[doc.currentVersion.externalLinkProvider]}`
                            : ''}
                          {doc.tags?.length ? ` · ${doc.tags.slice(0, 3).join(' · ')}` : ''}
                        </Typography>
                      }
                    />
                    <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
                      <Tooltip title="Ver ficha del documento" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDocInfo(doc);
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={isExternal ? 'Abrir archivo externo' : 'Abrir contenido'}
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDocContent(doc);
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </ListItemButton>
                );
              }
              return null;
            })}
          </List>
        ) : emptyHint ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="caption" color="textSecondary">
              {emptyHint}
            </Typography>
          </Box>
        ) : null}
        {navCount > 0 && docResults.length > 0 && (
          <Divider textAlign="left" sx={{ my: 0.5, fontSize: 10, color: 'text.secondary' }}>
            ↑ Navegación · ↓ Documentos
          </Divider>
        )}

        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Switch
                size="small"
                checked={includeCancelled}
                onChange={(e) => setIncludeCancelled(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="textSecondary">
                Incluir cancelados y archivados
              </Typography>
            }
          />
          <Stack direction="row" spacing={2}>
            <Typography variant="caption" color="textSecondary">
              ↑↓ navegar
            </Typography>
            <Typography variant="caption" color="textSecondary">
              ↵ abrir
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Esc cerrar
            </Typography>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
